import React, { useEffect, useRef, useState } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Wrench,
  Users,
  BarChart3,
  LayoutDashboard,
  ArrowRight,
  Moon,
  Sun,
  MapPin,
  ShieldCheck,
  Bell,
  ClipboardList,
  CheckCircle2,
  Zap,
  ChevronRight,
  Star,
} from 'lucide-react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { useTheme } from '../../context/ThemeContext';
import Button from '../../Components/Common/Button';
import Card from '../../Components/Common/Card';

gsap.registerPlugin(ScrollTrigger);

/* ─── Data ─────────────────────────────────────────────────────────────── */

const FEATURES = [
  {
    id: 1,
    icon: ClipboardList,
    title: 'Work Order Tracking',
    description:
      'Create, assign, and track work orders from submission to sign-off with real-time status updates and automated SLA alerts.',
    color: '#C9A76A',
    bg: 'rgba(201,167,106,0.12)',
  },
  {
    id: 2,
    icon: Users,
    title: 'Customer Management',
    description:
      'Centralize customer records, service history, and communication threads for a 360° view of every account.',
    color: '#8FA3B8',
    bg: 'rgba(143,163,184,0.12)',
  },
  {
    id: 3,
    icon: MapPin,
    title: 'Map-Based Dispatch',
    description:
      'Visualize open work orders on an interactive map and dispatch the nearest available technician in one click.',
    color: '#D9A441',
    bg: 'rgba(217,164,65,0.12)',
  },
  {
    id: 4,
    icon: BarChart3,
    title: 'Analytics & Reports',
    description:
      'Monitor KPIs, volume trends, and SLA compliance with live dashboards and scheduled export reports.',
    color: '#D8B97E',
    bg: 'rgba(201,167,106,0.12)',
  },
  {
    id: 5,
    icon: Bell,
    title: 'Smart Notifications',
    description:
      'Keep every stakeholder informed with automated alerts for assignment changes, SLA warnings, and completions.',
    color: '#D46A5E',
    bg: 'rgba(212,106,94,0.12)',
  },
  {
    id: 6,
    icon: ShieldCheck,
    title: 'Role-Based Access',
    description:
      'Define granular permissions per role so every team member sees exactly what they need — nothing more.',
    color: '#7FB08A',
    bg: 'rgba(127,176,138,0.12)',
  },
];

const STATS = [
  { value: '10K+', label: 'Work Orders Processed' },
  { value: '99.8%', label: 'Uptime SLA' },
  { value: '4 min', label: 'Avg. Dispatch Time' },
  { value: '350+', label: 'Utility Technicians' },
];

const TESTIMONIALS = [
  {
    name: 'Marcus T.',
    role: 'Operations Manager',
    company: 'CityWater Co.',
    avatar: 'MT',
    avatarColor: '#C9A76A',
    text: 'Vocal for Local cut our average dispatch time in half. The map view alone saved us hours every week.',
    rating: 5,
  },
  {
    name: 'Priya S.',
    role: 'Field Supervisor',
    company: 'Metro Gas Corp.',
    avatar: 'PS',
    avatarColor: '#8FA3B8',
    text: 'SLA alerts and real-time tracking finally gave us the visibility we desperately needed.',
    rating: 5,
  },
  {
    name: 'Jordan K.',
    role: 'IT Director',
    company: 'Eastside Utilities',
    avatar: 'JK',
    avatarColor: '#D9A441',
    text: 'Role-based access made onboarding easy. Our teams were productive from day one.',
    rating: 5,
  },
];

/* ─── Root Component ───────────────────────────────────────────────────── */

export default function Home() {
  const isLoggedIn = !!localStorage.getItem(process.env.REACT_APP_BACKEND_TOKEN_NAME);

  const navRef           = useRef(null);
  const heroRef          = useRef(null);
  const heroCardRef      = useRef(null);
  const statsRef         = useRef(null);
  const featuresSectionRef = useRef(null);
  const featuresGridRef  = useRef(null);
  const testimonialsRef  = useRef(null);

  const { isDark, toggleTheme } = useTheme();

  /* GSAP animations */
  useEffect(() => {
    const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });

    if (navRef.current) {
      tl.fromTo(navRef.current, { y: -80, opacity: 0 }, { y: 0, opacity: 1, duration: 0.7 });
    }

    if (heroRef.current) {
      tl.fromTo(
        heroRef.current.children,
        { y: 40, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.75, stagger: 0.14 },
        '-=0.4'
      );
    }

    if (heroCardRef.current) {
      tl.fromTo(
        heroCardRef.current,
        { x: 80, opacity: 0, scale: 0.92 },
        { x: 0, opacity: 1, scale: 1, duration: 1.1, ease: 'elastic.out(1, 0.8)' },
        '-=0.7'
      );
    }

    /* Stats counter strip */
    if (statsRef.current) {
      gsap.fromTo(
        statsRef.current.children,
        { y: 30, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.6, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: statsRef.current, start: 'top 85%' },
        }
      );
    }

    /* Features grid */
    if (featuresGridRef.current) {
      gsap.fromTo(
        featuresGridRef.current.children,
        { y: 50, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.7, stagger: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: featuresSectionRef.current, start: 'top 80%' },
        }
      );
    }

    /* Testimonials */
    if (testimonialsRef.current) {
      gsap.fromTo(
        testimonialsRef.current.children,
        { y: 40, opacity: 0 },
        {
          y: 0, opacity: 1, duration: 0.65, stagger: 0.12, ease: 'power3.out',
          scrollTrigger: { trigger: testimonialsRef.current, start: 'top 80%' },
        }
      );
    }
  }, []);

  /* ─── Theme values ─── */
  const bg        = isDark ? '#0B0B0C' : '#F6F2EA';
  const surface   = isDark ? '#141414' : '#FFFFFF';
  const border    = isDark ? '#2A2A2A' : '#E2D9C8';
  const text      = isDark ? '#F3EEE6' : '#17150F';
  const subtext   = isDark ? '#8C877F' : '#6A6357';
  const primary   = isDark ? '#C9A76A' : '#A87C2A';
  const primaryBg = isDark ? 'rgba(201,167,106,0.14)' : 'rgba(168,124,42,0.12)';

  return (
    <div
      className="min-h-screen flex flex-col font-sans antialiased selection:bg-[var(--accent-soft)] selection:text-[var(--accent)] transition-colors duration-200"
      style={{ backgroundColor: bg, color: text }}
    >

      {/* ════════════════════════════════════
          NAVBAR
      ════════════════════════════════════ */}
      <nav
        ref={navRef}
        className="sticky top-0 z-50 border-b transition-colors duration-200"
        style={{
          backgroundColor: isDark ? 'rgba(11,11,12,0.85)' : 'rgba(246,242,234,0.85)',
          borderColor: border,
          backdropFilter: 'blur(16px)',
          WebkitBackdropFilter: 'blur(16px)',
        }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 h-16 flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center shadow-sm"
              style={{ backgroundColor: primary }}
            >
              <Wrench className="w-[18px] h-[18px] text-[#14110B] dark:text-[#14110B]" />
            </div>
            <span className="text-xl font-black tracking-tight" style={{ color: text }}>
              Vocal for Local
            </span>
          </div>

          {/* Nav links (desktop) */}
          <div className="hidden md:flex items-center gap-6">
            {['Features', 'Analytics', 'Pricing'].map((item) => (
              <a
                key={item}
                href="#features"
                className="text-sm font-semibold transition-colors duration-150 no-underline"
                style={{ color: subtext }}
                onMouseEnter={(e) => (e.currentTarget.style.color = primary)}
                onMouseLeave={(e) => (e.currentTarget.style.color = subtext)}
              >
                {item}
              </a>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2.5">
            <button
              onClick={toggleTheme}
              aria-label="Toggle dark mode"
              className="w-9 h-9 rounded-xl flex items-center justify-center border transition-all duration-200"
              style={{
                backgroundColor: surface,
                borderColor: border,
                color: subtext,
              }}
            >
              {isDark ? <Sun className="w-4 h-4 text-[#C9A76A]" /> : <Moon className="w-4 h-4" />}
            </button>
            <Button
              as={NavLink}
              to={isLoggedIn ? '/dashboard' : '/login'}
              variant="primary"
              size="sm"
              iconRight={ArrowRight}
            >
              {isLoggedIn ? 'Dashboard' : 'Sign In'}
            </Button>
          </div>
        </div>
      </nav>

      {/* ════════════════════════════════════
          HERO
      ════════════════════════════════════ */}
      <section className="relative flex-1 overflow-hidden px-6 sm:px-8 py-20 sm:py-28">
        {/* Background blobs */}
        <div
          className="absolute top-[-15%] right-[-8%] w-[55%] h-[70%] rounded-full blur-[90px] -z-10 pointer-events-none"
          style={{
            background: isDark
              ? 'radial-gradient(circle, rgba(201,167,106,0.06) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(168,124,42,0.06) 0%, transparent 70%)',
          }}
        />
        <div
          className="absolute bottom-[-15%] left-[-8%] w-[45%] h-[60%] rounded-full blur-[90px] -z-10 pointer-events-none"
          style={{
            background: isDark
              ? 'radial-gradient(circle, rgba(201,167,106,0.04) 0%, transparent 70%)'
              : 'radial-gradient(circle, rgba(168,124,42,0.04) 0%, transparent 70%)',
          }}
        />

        <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">

          {/* ── Left: Text column ── */}
          <div ref={heroRef} className="lg:col-span-6 xl:col-span-7 flex flex-col items-start gap-6">

            {/* Eyebrow badge */}
            <span
              className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider border border-[var(--border-subtle)]"
              style={{
                backgroundColor: primaryBg,
                color: primary,
              }}
            >
              <Zap className="w-3.5 h-3.5" />
              Next-Gen Utility Operations
            </span>

            {/* Headline */}
            <h1
              className="text-4xl sm:text-5xl xl:text-6xl font-black tracking-tight leading-[1.08]"
              style={{ color: text }}
            >
              Smart Work Order{' '}
              <br className="hidden sm:block" />
              <span
                className="bg-clip-text text-transparent"
                style={{
                  backgroundImage: isDark
                    ? 'linear-gradient(135deg, #C9A76A 0%, #E5C388 100%)'
                    : 'linear-gradient(135deg, #A87C2A 0%, #8F6820 100%)',
                }}
              >
                Management System
              </span>
            </h1>

            {/* Subheadline */}
            <p
              className="text-base sm:text-lg font-medium leading-relaxed max-w-lg"
              style={{ color: subtext }}
            >
              Empower your utility teams to create, assign, and resolve work orders with real-time dispatch intelligence, SLA monitoring, and executive-level analytics — all in one platform.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
              <Button
                as={NavLink}
                to={isLoggedIn ? '/dashboard' : '/login'}
                variant="primary"
                size="lg"
                iconRight={ArrowRight}
              >
                {isLoggedIn ? 'Go to Dashboard' : 'Get Started Free'}
              </Button>

              <Button
                as={NavLink}
                to={isLoggedIn ? '/dashboard' : '/login'}
                variant="secondary"
                size="lg"
                icon={LayoutDashboard}
              >
                View Demo
              </Button>
            </div>

            {/* Trust badges */}
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 pt-2">
              {['No credit card required', 'SOC 2 Compliant', 'WCAG AA Accessible'].map((item) => (
                <div key={item} className="flex items-center gap-1.5">
                  <CheckCircle2 className="w-3.5 h-3.5 shrink-0" style={{ color: primary }} />
                  <span className="text-xs font-semibold" style={{ color: subtext }}>
                    {item}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Right: Analytics Preview Card ── */}
          <div
            ref={heroCardRef}
            className="lg:col-span-6 xl:col-span-5 flex justify-center lg:justify-end"
          >
            <HeroDashboardCard isDark={isDark} surface={surface} border={border} text={text} subtext={subtext} primary={primary} />
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          STATS STRIP
      ════════════════════════════════════ */}
      <section
        className="border-y transition-colors duration-300"
        style={{ backgroundColor: surface, borderColor: border }}
      >
        <div className="max-w-7xl mx-auto px-6 sm:px-8 py-10">
          <div
            ref={statsRef}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0 md:divide-x"
            style={{ '--tw-divide-opacity': 1 }}
          >
            {STATS.map((s) => (
              <div key={s.label} className="flex flex-col items-center text-center px-4">
                <span
                  className="text-3xl sm:text-4xl font-black tracking-tight tabular-nums"
                  style={{ color: primary }}
                >
                  {s.value}
                </span>
                <span
                  className="text-xs font-semibold uppercase tracking-wider mt-1.5"
                  style={{ color: subtext }}
                >
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          FEATURES
      ════════════════════════════════════ */}
      <section
        id="features"
        ref={featuresSectionRef}
        className="py-24 px-6 sm:px-8 transition-colors duration-300"
        style={{ backgroundColor: bg }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Section header */}
          <div className="text-center max-w-xl mx-auto mb-16">
            <span
              className="text-[11px] font-black uppercase tracking-widest"
              style={{ color: primary }}
            >
              Core Capabilities
            </span>
            <h2
              className="text-3xl sm:text-4xl font-black tracking-tight mt-3"
              style={{ color: text }}
            >
              Everything your team needs
            </h2>
            <p className="text-sm font-medium leading-relaxed mt-4" style={{ color: subtext }}>
              Built for field-service operations teams who need reliability, speed, and clarity at every step.
            </p>
            <div
              className="w-10 h-1 rounded-full mx-auto mt-6"
              style={{ backgroundColor: primary }}
            />
          </div>

          {/* Feature cards grid */}
          <div
            ref={featuresGridRef}
            className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3"
          >
            {FEATURES.map((f) => (
              <FeatureCard key={f.id} feature={f} isDark={isDark} surface={surface} border={border} text={text} subtext={subtext} primary={primary} />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          TESTIMONIALS
      ════════════════════════════════════ */}
      <section
        className="py-24 px-6 sm:px-8 border-t transition-colors duration-300"
        style={{ backgroundColor: surface, borderColor: border }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center max-w-xl mx-auto mb-14">
            <span className="text-[11px] font-black uppercase tracking-widest" style={{ color: primary }}>
              What Teams Say
            </span>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tight mt-3" style={{ color: text }}>
              Trusted by field-service teams
            </h2>
          </div>

          {/* Cards */}
          <div
            ref={testimonialsRef}
            className="grid gap-5 sm:grid-cols-2 xl:grid-cols-3"
          >
            {TESTIMONIALS.map((t) => (
              <TestimonialCard
                key={t.name}
                t={t}
                isDark={isDark}
                bg={bg}
                border={border}
                text={text}
                subtext={subtext}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ════════════════════════════════════
          CTA BANNER
      ════════════════════════════════════ */}
      <section className="py-20 px-6 sm:px-8 transition-colors duration-300" style={{ backgroundColor: bg }}>
        <Card
          className="max-w-4xl mx-auto p-10 sm:p-16 text-center relative overflow-hidden"
          style={{
            background: isDark
              ? 'linear-gradient(135deg, #141414 0%, #1A1916 100%)'
              : 'linear-gradient(135deg, #FFFFFF 0%, #FBF9F5 100%)',
          }}
        >
          {/* Decorative gold ambient glow */}
          <div
            className="absolute top-0 right-0 w-64 h-64 rounded-full blur-3xl -z-0 pointer-events-none"
            style={{
              background: isDark
                ? 'rgba(201, 167, 106, 0.08)'
                : 'rgba(168, 124, 42, 0.07)',
            }}
          />
          <div className="relative z-10">
            <div
              className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-md border border-[var(--border-subtle)]"
              style={{ backgroundColor: primaryBg, color: primary }}
            >
              <Zap className="w-7 h-7" />
            </div>
            <h2
              className="text-3xl sm:text-4xl font-black tracking-tight mb-4"
              style={{ color: text }}
            >
              Ready to transform your operations?
            </h2>
            <p
              className="text-sm sm:text-base font-medium leading-relaxed max-w-lg mx-auto mb-8"
              style={{ color: subtext }}
            >
              Join hundreds of utility teams using Vocal for Local to dispatch faster, resolve smarter, and report with confidence.
            </p>
            <Button
              as={NavLink}
              to={isLoggedIn ? '/dashboard' : '/login'}
              variant="primary"
              size="lg"
              iconRight={ArrowRight}
            >
              {isLoggedIn ? 'Open Dashboard' : 'Start for Free'}
            </Button>
          </div>
        </Card>
      </section>

      {/* ════════════════════════════════════
          FOOTER
      ════════════════════════════════════ */}
      <HomeFooter isDark={isDark} surface={surface} border={border} text={text} subtext={subtext} primary={primary} />
    </div>
  );
}

/* ─── Hero Dashboard Preview Card ─────────────────────────────────────── */
function HeroDashboardCard({ isDark, surface, border, text, subtext, primary }) {
  const stats = [
    { label: 'Completed', value: '27', color: isDark ? '#7FB08A' : '#3E7A4F', icon: CheckCircle2 },
    { label: 'Pending', value: '4', color: isDark ? '#D9A441' : '#9A6A12', icon: ClipboardList },
    { label: 'SLA Rate', value: '97%', color: primary, icon: ShieldCheck },
    { label: 'Active Teams', value: '8', color: isDark ? '#8FA3B8' : '#4B6480', icon: Users },
  ];

  return (
    <Card
      variant="default"
      className="relative w-full max-w-[400px] overflow-hidden shadow-2xl p-0"
    >
      {/* Card top bar */}
      <div
        className="flex items-center justify-between px-5 py-4 border-b"
        style={{ borderColor: border }}
      >
        <div className="flex items-center gap-3">
          <div
            className="w-9 h-9 rounded-xl flex items-center justify-center"
            style={{ backgroundColor: primary }}
          >
            <LayoutDashboard className="w-4.5 h-4.5 text-[#14110B] dark:text-[#14110B]" style={{ width: 18, height: 18 }} />
          </div>
          <div>
            <p className="text-[13px] font-bold m-0" style={{ color: text }}>
              Operations Overview
            </p>
            <div className="flex items-center gap-1.5 mt-0.5">
              <div className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ backgroundColor: primary }} />
              <p className="text-[11px] font-medium m-0" style={{ color: subtext }}>
                Live
              </p>
            </div>
          </div>
        </div>
        {/* Indicators */}
        <div className="flex items-center gap-1.5">
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: isDark ? '#2A2A2A' : '#E2D9C8' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: isDark ? '#5E5A54' : '#A39B8B' }} />
          <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: primary }} />
        </div>
      </div>

      {/* Stats grid */}
      <div className="p-5 grid grid-cols-2 gap-3">
        {stats.map((s) => {
          const SIcon = s.icon;
          return (
            <div
              key={s.label}
              className="card-border-subtle p-4"
            >
              <div className="flex items-center justify-between mb-2">
                <div
                  className="w-7 h-7 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${s.color}22` }}
                >
                  <SIcon className="w-3.5 h-3.5" style={{ color: s.color }} />
                </div>
                <ChevronRight className="w-3.5 h-3.5" style={{ color: subtext, opacity: 0.5 }} />
              </div>
              <p
                className="text-2xl font-black tracking-tight leading-none tabular-nums"
                style={{ color: s.color }}
              >
                {s.value}
              </p>
              <p
                className="text-[11px] font-semibold uppercase tracking-wider mt-1"
                style={{ color: subtext }}
              >
                {s.label}
              </p>
            </div>
          );
        })}
      </div>

      {/* Activity bar */}
      <div
        className="px-5 pb-5"
        style={{ borderTop: `1px solid ${border}`, paddingTop: 16 }}
      >
        <p className="text-[11px] font-bold uppercase tracking-wider mb-3" style={{ color: subtext }}>
          Weekly Volume
        </p>
        <div className="flex items-end gap-1.5 h-12">
          {[40, 65, 45, 80, 55, 90, 70].map((h, i) => (
            <div
              key={i}
              className="flex-1 rounded-sm transition-all duration-300"
              style={{
                height: `${h}%`,
                backgroundColor: i === 5
                  ? primary
                  : isDark ? 'rgba(201,167,106,0.18)' : 'rgba(168,124,42,0.14)',
              }}
            />
          ))}
        </div>
        <div className="flex justify-between mt-1.5">
          {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => (
            <span key={i} className="flex-1 text-center text-[9px] font-bold" style={{ color: subtext }}>
              {d}
            </span>
          ))}
        </div>
      </div>
    </Card>
  );
}

/* ─── Feature Card ─────────────────────────────────────────────────────── */
function FeatureCard({ feature, isDark, surface, border, text, subtext, primary }) {
  const Icon = feature.icon;
  const [hovered, setHovered] = useState(false);

  return (
    <Card
      variant="interactive"
      className="p-7 cursor-default group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Icon */}
      <div
        className="w-12 h-12 rounded-2xl flex items-center justify-center mb-5 transition-all duration-200 border border-[var(--border-subtle)]"
        style={{
          backgroundColor: hovered ? feature.color : feature.bg,
        }}
      >
        <Icon
          className="w-6 h-6 transition-colors duration-200"
          style={{ color: hovered ? '#FFFFFF' : feature.color }}
        />
      </div>

      <h3
        className="text-base font-bold mb-2 transition-colors duration-200"
        style={{ color: hovered ? feature.color : text }}
      >
        {feature.title}
      </h3>
      <p className="text-sm font-medium leading-relaxed m-0" style={{ color: subtext }}>
        {feature.description}
      </p>

      {/* Arrow link */}
      <div
        className="flex items-center gap-1 mt-4 text-[11px] font-bold uppercase tracking-wider transition-all duration-200"
        style={{
          color: hovered ? feature.color : isDark ? '#8C877F' : '#A39B8B',
        }}
      >
        <span>Learn more</span>
        <ArrowRight className="w-3 h-3" />
      </div>
    </Card>
  );
}

/* ─── Testimonial Card ─────────────────────────────────────────────────── */
function TestimonialCard({ t, isDark, bg, border, text, subtext }) {
  return (
    <Card variant="interactive" className="p-7">
      {/* Stars */}
      <div className="flex gap-1 mb-5">
        {Array.from({ length: t.rating }).map((_, i) => (
          <Star key={i} className="w-4 h-4 fill-[var(--warning)] text-[var(--warning)]" />
        ))}
      </div>

      {/* Quote */}
      <p
        className="text-sm font-medium leading-relaxed mb-6"
        style={{ color: text }}
      >
        "{t.text}"
      </p>

      {/* Author */}
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold text-white shrink-0 border border-[var(--border-subtle)]"
          style={{ backgroundColor: t.avatarColor }}
        >
          {t.avatar}
        </div>
        <div>
          <p className="text-[13px] font-bold m-0" style={{ color: text }}>
            {t.name}
          </p>
          <p className="text-[11px] font-medium m-0 mt-0.5" style={{ color: subtext }}>
            {t.role} · {t.company}
          </p>
        </div>
      </div>
    </Card>
  );
}

/* ─── Footer ───────────────────────────────────────────────────────────── */
function HomeFooter({ isDark, surface, border, text, subtext, primary }) {
  const links = [
    { group: 'Product', items: ['Features', 'Analytics', 'Map View', 'Pricing'] },
    { group: 'Company', items: ['About', 'Careers', 'Blog', 'Contact'] },
    { group: 'Legal', items: ['Privacy', 'Terms', 'Cookie Policy'] },
  ];

  return (
    <footer
      className="border-t py-16 px-6 sm:px-8 transition-colors duration-300"
      style={{ backgroundColor: surface, borderColor: border }}
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-10 mb-12">
          {/* Brand column */}
          <div className="md:col-span-1">
            <div className="flex items-center gap-3 mb-4">
              <div
                className="w-9 h-9 rounded-xl flex items-center justify-center"
                style={{ backgroundColor: primary }}
              >
                <Wrench className="w-[18px] h-[18px] text-white" />
              </div>
              <span className="text-lg font-black tracking-tight" style={{ color: text }}>
                Vocal for Local
              </span>
            </div>
            <p className="text-sm font-medium leading-relaxed" style={{ color: subtext }}>
              High-efficiency work order management for modern utility operations teams.
            </p>
          </div>

          {/* Link columns */}
          {links.map((group) => (
            <div key={group.group}>
              <p
                className="text-[11px] font-black uppercase tracking-widest mb-4"
                style={{ color: text }}
              >
                {group.group}
              </p>
              <ul className="flex flex-col gap-2.5 list-none m-0 p-0">
                {group.items.map((item) => (
                  <li key={item}>
                    <a
                      href="#features"
                      className="text-sm font-medium no-underline transition-colors duration-150"
                      style={{ color: subtext }}
                      onMouseEnter={(e) => (e.currentTarget.style.color = primary)}
                      onMouseLeave={(e) => (e.currentTarget.style.color = subtext)}
                    >
                      {item}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom bar */}
        <div
          className="pt-8 border-t flex flex-col sm:flex-row items-center justify-between gap-3"
          style={{ borderColor: border }}
        >
          <p className="text-[12px] font-medium" style={{ color: subtext }}>
            © {new Date().getFullYear()} Vocal for Local Portal. Built for high-efficiency utility management.
          </p>
          <div className="flex items-center gap-1.5">
            <div className="w-2 h-2 rounded-full bg-[var(--success)]" />
            <p className="text-[12px] font-semibold" style={{ color: subtext }}>
              All systems operational
            </p>
          </div>
        </div>
      </div>
    </footer>
  );
}
